/**
 * 多语言翻译开关 / 灰度策略工具
 *
 * 设计目标：
 *   把"当前用户/流程是否应该看到语言切换按钮"的判断逻辑独立出来，
 *   不绑定到具体 Vue 组件，方便在 LanguageSwitcher 以及其他场景（如路由守卫、
 *   入口拦截、其他业务页面引入按钮）复用。
 *
 * 涉及的七彩石配置项：
 *   - multi_language_info  : { open: boolean, key: string[] }
 *                            open 总开关；key 流程白名单（ProcessDefinitionKey）
 *   - useGray              : number(0|1) 灰度模式
 *                            0 = 只做流程标识灰度（对所有用户可见）
 *                            1 = 走用户灰度策略（系统语言非中文 || 用户在白名单）
 *   - multi_language_user  : { user: string[] } 用户白名单（账号）
 *
 * 启用规则：
 *   流程必须在 multi_language_info.key 白名单中（基础门槛）。
 *   在此之上：
 *     - useGray === 0 → 直接展示（对所有用户）
 *     - useGray === 1 → 满足以下任一即展示：
 *         1) 系统语言非中文（navigator.language 不以 zh 开头）
 *         2) 当前用户在 multi_language_user.user 白名单内
 *
 * 用法：
 *   import { canUseTranslate } from '@/utils/translateGate';
 *   const ok = canUseTranslate(store, processDefinitionKey);
 *
 * 也可单独使用底层判断：
 *   import { isSystemLangNonChinese, isUserInWhiteList } from '@/utils/translateGate';
 */

/* -------------------------- 配置 key 常量 -------------------------- */

export const TRANSLATE_GATE_CFG_KEYS = {
  /** 翻译总开关 + 流程白名单：{ open: boolean, key: string[] } */
  INFO: 'multi_language_info',
  /** 灰度模式：0 = 全量；1 = 走用户灰度 */
  USE_GRAY: 'useGray',
  /** 用户白名单：{ user: string[] } */
  USER_WHITELIST: 'multi_language_user',
};

/* -------------------------- 工具函数 -------------------------- */

/**
 * 安全解析 JSON：失败 / 输入为空时返回 fallback；输入已是对象则直接返回。
 */
function safeParse(raw, fallback) {
  if (raw === undefined || raw === null || raw === '') return fallback;
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return fallback;
  }
}

/**
 * 从 store 读取一个七彩石配置项；store 为空 / getter 不存在时返回空字符串。
 *
 * @param {Object} store Vuex store 实例（可由调用方传入；缺省时回退到 import 的全局 store）
 * @param {string} key   配置 key
 * @returns {*} 原始配置值（一般是字符串 JSON 或基础类型）
 */
function readConfig(store, key) {
  try {
    if (store && store.getters && typeof store.getters.getConfigItem === 'function') {
      return store.getters.getConfigItem(key);
    }
  } catch (e) { /* ignore */ }
  return '';
}

/**
 * 获取当前用户 ID（来自 sessionStorage.userid，与项目其他地方保持一致）
 * @returns {string}
 */
export function getCurrentUserId() {
  try {
    if (typeof sessionStorage !== 'undefined') {
      return sessionStorage.getItem('userid') || '';
    }
  } catch (e) { /* ignore */ }
  return '';
}

/**
 * 判断系统语言是否"非中文"
 *   - 中文判定：navigator.language 以 'zh' 开头（含 zh-CN / zh-TW 等）
 *   - 非中文返回 true（即"应该展示翻译按钮"的条件之一）
 *
 * @returns {boolean}
 */
export function isSystemLangNonChinese() {
  try {
    if (typeof navigator === 'undefined') return false;
    const lang = (navigator.language || navigator.userLanguage || '').toLowerCase();
    if (!lang) return false;
    return !lang.startsWith('zh');
  } catch (e) {
    return false;
  }
}

/**
 * 判断当前用户是否在多语言用户白名单内。
 *
 * @param {Object} store        Vuex store
 * @param {string} [currentUid] 可选：传入用户ID（否则从 sessionStorage 读取）
 * @returns {boolean}
 */
export function isUserInWhiteList(store, currentUid) {
  const uid = currentUid || getCurrentUserId();
  if (!uid) return false;
  const raw = readConfig(store, TRANSLATE_GATE_CFG_KEYS.USER_WHITELIST);
  const parsed = safeParse(raw, null);
  if (!parsed) return false;
  const users = Array.isArray(parsed.user) ? parsed.user
    : (Array.isArray(parsed) ? parsed : []);
  return users.includes(uid);
}

/**
 * 判断当前流程标识是否在白名单内。
 *
 * @param {Object} store
 * @param {string} processDefinitionKey
 * @returns {boolean}
 */
export function isProcessInWhiteList(store, processDefinitionKey) {
  if (!processDefinitionKey) return false;
  const raw = readConfig(store, TRANSLATE_GATE_CFG_KEYS.INFO);
  const info = safeParse(raw, { open: false, key: [] });
  if (!info || !info.open) return false;
  const keys = Array.isArray(info.key) ? info.key : [];
  return keys.includes(processDefinitionKey);
}

/**
 * 读取灰度模式：默认 0（流程灰度）。
 * @param {Object} store
 * @returns {number}  0 | 1
 */
export function getUseGrayMode(store) {
  const raw = readConfig(store, TRANSLATE_GATE_CFG_KEYS.USE_GRAY);
  // 兼容：number / "1" / "0" / boolean
  if (typeof raw === 'number') return raw === 1 ? 1 : 0;
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number(raw);
    return n === 1 ? 1 : 0;
  }
  if (typeof raw === 'boolean') return raw ? 1 : 0;
  return 0;
}

/* -------------------------- 主入口 -------------------------- */

/**
 * 综合判断：当前用户是否可以看到语言切换按钮（即应该启用翻译功能）。
 *
 * 规则：
 *   1) 流程必须在白名单内（multi_language_info.key 含 processDefinitionKey）
 *   2) useGray 模式：
 *      - 0：通过（流程灰度，对所有用户可见）
 *      - 1：当"系统语言非中文" 或 "用户在白名单"，二者任一满足时通过
 *
 * 特殊场景（待办列表/已办列表等无流程标识的页面）：
 *   传入 opts.forceUserGray = true 时，无论配置中 useGray 为何值，
 *   都强制走 useGray=1 的逻辑（必须系统语言非中文 || 用户在白名单）。
 *
 * @param {Object} store                 Vuex store（必传，从中读取七彩石配置）
 * @param {string} processDefinitionKey  当前业务流程标识
 * @param {Object} [opts]
 * @param {string} [opts.userId]         可选：显式传入用户 ID（缺省从 sessionStorage 读）
 * @param {boolean} [opts.forceUserGray] 可选：强制走用户灰度（useGray=1）逻辑
 * @returns {boolean}
 */
export function canUseTranslate(store, processDefinitionKey, opts = {}) {
  // 1) 流程白名单：基础门槛
  //    如果传了 processDefinitionKey 则必须命中；没传则跳过流程检查（全局模式）
  if (processDefinitionKey && !isProcessInWhiteList(store, processDefinitionKey)) return false;
  // 没传 processDefinitionKey 但有流程白名单要求时：检查总开关是否开启
  if (!processDefinitionKey) {
    const raw = readConfig(store, TRANSLATE_GATE_CFG_KEYS.INFO);
    const info = safeParse(raw, { open: false, key: [] });
    if (!info || !info.open) return false;
  }

  // 2) 灰度模式
  const useGray = getUseGrayMode(store);

  // 强制走用户灰度（待办列表/已办列表等无流程标识的页面）
  if (opts.forceUserGray) {
    const nonChinese = isSystemLangNonChinese();
    const inWhite = isUserInWhiteList(store, opts.userId);
    return nonChinese || inWhite;
  }

  if (useGray === 0) {
    // 流程灰度模式：流程命中即对所有用户开放
    return true;
  }

  // useGray === 1：用户灰度模式
  const nonChinese = isSystemLangNonChinese();
  const inWhite = isUserInWhiteList(store, opts.userId);
  return nonChinese || inWhite;
}

export default canUseTranslate;
